import { createSlice } from "@reduxjs/toolkit";
import {
  AddAIResponse,
  deleteChatSession,
  editChatSessionTitle,
  getChatHistory,
  getChatSessionHistory,
  likeDislikeMessage,
  streamAIResponse 
} from "./thunk";
import { toast } from "react-toastify";
import { t } from "i18next";
import { Status } from "filepond";
import { Title } from "chart.js";
import { fetchClientUsers } from "../thunks";

const initialState = {
  aiResponseLoading: false,
  aiResponseError: null,
  streamingStatusMessage: "", 
  chatSessionHistoryLoading: false,
  chatSessionHistoryError: null,
  chatSessionHistory: null,
  messages: [],
  messagesLoading: false,
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
  hasMoreMessages: true,
  messagesError: null,
  likeDislikeLoading: false,
  likeDislikeError: null,
  ClientUsers: [],
  ClientUsersLoading: false,
};

const AIChatSlice = createSlice({
  name: "AIChat",
  initialState,
  reducers: {
    // Reducer to add a message to the messages array
    addMessage: (state, action) => {
      state.messages.push(action.payload);
    },
    // NEW reducer to update the content of the streaming message
    updateStreamingMessage: (state, action) => {
      const lastMessage = state.messages[state.messages.length - 1];
      if (lastMessage && lastMessage.isStreaming) {
        lastMessage.Message = action.payload.text;
      }
    },

    // NEW reducer to append text to the streaming message
    appendStreamingMessage: (state, action) => {
      const lastMessage = state.messages[state.messages.length - 1];
      if (lastMessage && lastMessage.isStreaming) {
        lastMessage.Message += action.payload.text;
      }
    },

    // NEW reducer to replace the temporary message with the final one from DB
    finalizeStreamingMessage: (state, action) => {
        const { tempId, finalMessage } = action.payload;

        const messageIndex = state.messages.findIndex(m => m.ID === tempId);
        if (messageIndex !== -1) {
            console.log("✅ Match found! Replacing message at index", messageIndex);
          
            // Replace the placeholder with the final, complete message
            state.messages[messageIndex] = { ...finalMessage, isStreaming: false };
        }else {
            console.warn("❌ No match found for tempId:", tempId);
        }
        // If the chat was new, the finalMessage will contain the new SessionID
        // Update all messages in this session with the new SessionID
        if (finalMessage.SessionID && state.messages.some(m => !m.SessionID)) {
            state.messages.forEach(m => {
                if (!m.SessionID) {
                    m.SessionID = finalMessage.SessionID;
                }
            });
        }
    },
    // Reducer to clear messages (e.g., when starting a new chat session)
    clearMessages: (state) => {
      state.messages = [];
      state.currentPage = 1;
      state.totalPages = 1;
      state.totalCount = 0;
      state.hasMoreMessages = true;
    },
    // NEW: Reducer to set the streaming status message
    setStreamingStatus: (state, action) => {
      state.streamingStatusMessage = action.payload;
    },
    
  },
  extraReducers: (builder) => {
    builder
      // Stream AI Response API
      .addCase(streamAIResponse.pending, (state) => {
        state.aiResponseLoading = true;
        state.aiResponseError = null;
        state.streamingStatusMessage = "Connecting..."; 
      })
      .addCase(streamAIResponse.fulfilled, (state, action) => {
        state.aiResponseLoading = false;
        state.streamingStatusMessage = "";
        // If it was the first message, a new session is created. Refresh history.
        if (action.payload.isFirstMessage) {
            // This is an optimistic update. A better way would be to dispatch getChatSessionHistory
            // For now, we add it manually to the top of the list
            if (state.chatSessionHistory) {
                state.chatSessionHistory.unshift(action.payload.sessionInfo);
            }
        }
      })
      .addCase(streamAIResponse.rejected, (state, action) => {
        state.aiResponseLoading = false;
        state.streamingStatusMessage = "";
        state.aiResponseError = action.payload;
      })
      
      // Get Chat Session History API
      .addCase(getChatSessionHistory.pending, (state) => {
        state.chatSessionHistoryLoading = true;
        state.chatSessionHistoryError = null;
        state.chatSessionHistory = null;
      })
      .addCase(getChatSessionHistory.fulfilled, (state, action) => {
        state.chatSessionHistoryLoading = false;
        state.chatSessionHistory = action.payload;
      })
      .addCase(getChatSessionHistory.rejected, (state, action) => {
        state.chatSessionHistoryLoading = false;
        state.chatSessionHistoryError = action.payload;
      })

      // Get Chat History API
      .addCase(getChatHistory.pending, (state) => {
        state.messagesLoading = true;
        state.messagesError = null;
      })
      .addCase(getChatHistory.fulfilled, (state, action) => {
        state.messagesLoading = false;
        // state.messages = action.payload.messages;
        state.messages = [...action.payload.messages, ...state.messages]; 
        state.currentPage = action.payload.pagination.currentPage;
        state.totalPages = action.payload.pagination.totalPages;
        state.totalCount = action.payload.pagination.totalCount;
        state.hasMoreMessages = action.payload.pagination.currentPage < action.payload.pagination.totalPages;
      })
      .addCase(getChatHistory.rejected, (state, action) => {
        state.messagesLoading = false;
        state.messagesError = action.payload;
      })

      // Get Chat History API
      .addCase(likeDislikeMessage.pending, (state) => {
        state.likeDislikeLoading = true;
        state.likeDislikeError = null;
      })
      .addCase(likeDislikeMessage.fulfilled, (state, action) => {
        state.likeDislikeLoading = false;
        state.messages = state.messages.map((message) => {
          if (message.ID == action.payload.ID) {
            return {
              ...message,
              IsLiked: action.payload.IsLiked,
              FeedbackMessage: action.payload.FeedbackMessage
            };
          }
          return message;
        });
        // toast.success(t("Message updated successfully"));
      })
      .addCase(likeDislikeMessage.rejected, (state, action) => {
        state.likeDislikeLoading = false;
        state.likeDislikeError = action.payload;
      })

      // Edit Chat Session Title API
      .addCase(editChatSessionTitle.pending, (state) => {
        state.messagesLoading = true;
        state.messagesError = null;
      })
      .addCase(editChatSessionTitle.fulfilled, (state, action) => {
        state.messagesLoading = false;
        const updatedSession = action.payload;
        const sessionIndex = state.chatSessionHistory.findIndex(
          (session) => session.SessionID === updatedSession.SessionID
        );
        if (sessionIndex !== -1) {
          state.chatSessionHistory[sessionIndex].Title = updatedSession.Title;
        }
      })

      // Delete Chat Session API
      .addCase(deleteChatSession.pending, (state) => {
        state.messagesLoading = true;
        state.messagesError = null;
      })
      .addCase(deleteChatSession.fulfilled, (state, action) => {
        state.messagesLoading = false;
        const deletedSessionId = action.payload.SessionID;
        
        state.chatSessionHistory = state.chatSessionHistory.filter(
          (session) => session.SessionID !== deletedSessionId
        );
      })
      .addCase(deleteChatSession.rejected, (state, action) => {
        state.messagesLoading = false;
        state.messagesError = action.payload;
        toast.error("Failed to delete chat session: " + action.payload);
      })

      // Add Ai responce 
      .addCase(AddAIResponse.fulfilled, (state, action) => {
        //Just chage the session ID action.payload.SessionID
        const { SessionID, ID, IsFirstMessage, UserMessage } = action.payload;
        if (IsFirstMessage) {
          const date = new Date().toISOString();

          const newSession = {
            SessionID: SessionID,
            LastMessage: UserMessage,
            LastMessageAt: date,
            AIResponse: null,
            CreatedAt: date,
            Status: 1,
            Title: UserMessage
          };
          state.chatSessionHistory.unshift(newSession);
        }
        const lastKey = Object.keys(state.messages).pop();

        if (lastKey) {
          state.messages[lastKey].SessionID = SessionID;
          state.messages[lastKey].ID = ID;
        }
      })

      .addCase(fetchClientUsers.pending, (state) => {
        state.ClientUsersLoading = true;
        state.ClientUsers = [];
      })
      .addCase(fetchClientUsers.fulfilled, (state, action) => {
        state.ClientUsersLoading = false;
        state.ClientUsers = action.payload;
      })
      .addCase(fetchClientUsers.rejected, (state, action) => {
        state.ClientUsersLoading = false;
        state.ClientUsers = [];
      });

  },
});

export const { addMessage, clearMessages, updateStreamingMessage, finalizeStreamingMessage, setStreamingStatus } = AIChatSlice.actions; // Export the new actions
export default AIChatSlice.reducer;
